import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Button,
  Flex,
  Spacer,
  Badge,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Circle,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import {
  MdSearch,
  MdFilterList,
  MdEdit,
  MdAdd,
  MdRefresh,
  MdVerifiedUser,
  MdVisibility,
  MdTask,
  MdMoreVert,
  MdPersonRemove,
  MdDelete,
} from "react-icons/md";
import { useTaskStats, useTaskOperations } from "../hooks/useTasks";
import { useBatchStats } from "../hooks/useBatches";
import { handleApiError, handleApiSuccess } from "../utils/apiHelpers";
import { useData } from "../context/Context";
import ConfirmationDialog from "../components/common/ConfirmationDialog";
import { log } from "three/src/utils.js";

const TaskRow = ({
  task,
  isSelected,
  onToggle,
  userRole,
  onTaskClick,
  onDeleteTask,
  onUnassignTask,
}) => {
  console.log(task);

  const getStatusColor = (isVerified) => {
    return isVerified ? "green" : "orange";
  };

  const getStatusText = (isVerified) => {
    return isVerified ? "Completed" : "In Progress";
  };

  const getTypeColor = (taskType) => {
    return taskType === "classification" ? "blue" : "purple";
  };

  const formatProgress = (done, total) => {
    if (!total || total === 0) return "0/0";
    return `${done || 0}/${total}`;
  };

  const formatValue = (value) => {
    return value || value === 0 ? value : "-";
  };

  const formatBatchNames = (batchNames) => {
    if (!batchNames) {
      return "No batches";
    }

    // If the text is too long, truncate it and show full text on hover
    if (batchNames.length > 30) {
      return <span title={batchNames}>{batchNames.substring(0, 30)}...</span>;
    }

    return batchNames;
  };

  const handleRowClick = (e) => {
    // Don't trigger row click if clicking on checkbox or action buttons
    if (e.target.type === "checkbox" || e.target.closest("button")) {
      return;
    }
    onTaskClick(task); // Pass the full task object instead of just task_id
  };

  const renderActions = () => {
    if (userRole === "Admin") {
      return (
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="More actions"
            icon={<MdMoreVert />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            color="gray.400"
            _hover={{ color: "#FF9900", bg: "gray.700" }}
          />
          <MenuList bg="#1C1C1C" borderColor="#3A3A3A" borderWidth="1px">
            <MenuItem
              icon={<MdPersonRemove />}
              bg="transparent"
              color="orange.400"
              _hover={{ bg: "#2A2A2A" }}
              onClick={(e) => {
                e.stopPropagation();
                onUnassignTask(task);
              }}
            >
              Unassign
            </MenuItem>
            <MenuItem
              icon={<MdDelete />}
              bg="transparent"
              color="red.400"
              _hover={{ bg: "#2A2A2A" }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteTask(task);
              }}
            >
              Delete
            </MenuItem>
          </MenuList>
        </Menu>
      );
    } else if (userRole === "Reviewer") {
      return (
        <HStack spacing={2}>
          <IconButton
            aria-label="Verify task"
            icon={<MdVerifiedUser />}
            size="sm"
            variant="ghost"
            colorScheme="green"
            color="gray.400"
            _hover={{ color: "#10B981", bg: "gray.700" }}
          />
          <IconButton
            aria-label="View task"
            icon={<MdVisibility />}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            color="gray.400"
            _hover={{ color: "#3B82F6", bg: "gray.700" }}
          />
        </HStack>
      );
    } else if (userRole === "Annotator") {
      return (
        <IconButton
          aria-label="Work on task"
          icon={<MdTask />}
          size="sm"
          variant="ghost"
          colorScheme="blue"
          color="gray.400"
          _hover={{ color: "#3B82F6", bg: "gray.700" }}
        />
      );
    }
    return null;
  };

  return (
    <Tr
      bg="#121212"
      borderColor="#3A3A3A"
      _hover={{ bg: "#1A1A1A" }}
      onClick={handleRowClick}
      cursor="pointer"
    >
      <Td borderColor="#3A3A3A" py="4">
        <Checkbox
          isChecked={isSelected}
          onChange={onToggle}
          colorScheme="orange"
        />
      </Td>
      <Td borderColor="#3A3A3A" color="white" fontWeight="medium">
        {task.task_id ? `Task ${task.task_id.slice(0, 8)}...` : "Unknown Task"}
      </Td>
      <Td borderColor="#3A3A3A" color="gray.300" textAlign="center">
        <Badge
          colorScheme={getTypeColor(task.task_type)}
          variant="solid"
          borderRadius="md"
        >
          {task.task_type?.charAt(0).toUpperCase() + task.task_type?.slice(1) ||
            "Unknown"}
        </Badge>
      </Td>
      <Td borderColor="#3A3A3A" color="gray.300" textAlign="center">
        {formatBatchNames(task.batch_name)}
      </Td>
      <Td borderColor="#3A3A3A" color="gray.300" textAlign="center">
        {formatValue(task.total_models)}
      </Td>
      {(userRole === "Admin" || userRole === "Reviewer") && (
        <Td borderColor="#3A3A3A" color="gray.300" textAlign="center">
          {task.annotated_by || task.reviewed_by || "Unassigned"}
        </Td>
      )}
      <Td borderColor="#3A3A3A" color="gray.300" textAlign="center">
        {task.task_type === "classification"
          ? `Classified - ${formatProgress(
              task.done_models,
              task.total_models,
            )}`
          : `Aligned - ${formatProgress(task.done_models, task.total_models)}`}
      </Td>
      <Td borderColor="#3A3A3A" textAlign="center">
        <Badge
          colorScheme={getStatusColor(task.is_verified)}
          variant="solid"
          borderRadius="md"
        >
          {getStatusText(task.is_verified)}
        </Badge>
      </Td>
      <Td borderColor="#3A3A3A">{renderActions()}</Td>
    </Tr>
  );
};

const CreateTaskModal = ({ isOpen, onClose, onTaskCreated }) => {
  const [taskType, setTaskType] = useState("");
  const [selectedBatches, setSelectedBatches] = useState(new Set());
  const [searchBatch, setSearchBatch] = useState("");

  const { batches: batchData, loading: batchLoading } = useBatchStats();
  const { createTask, loading: creating } = useTaskOperations();

  // Filter batches based on search term
  const filteredBatchData = batchData.filter((batch) =>
    batch.batch_name?.toLowerCase().includes(searchBatch.toLowerCase()),
  );
  console.log(batchData);

  const handleBatchToggle = (batchId) => {
    console.log("Toggling batch with ID:", batchId);
    const newSelected = new Set(selectedBatches);
    if (newSelected.has(batchId)) {
      newSelected.delete(batchId);
    } else {
      newSelected.add(batchId);
    }
    setSelectedBatches(newSelected);
    console.log("Selected batch IDs:", Array.from(newSelected));
  };

  const handleCreateTask = async () => {
    if (!taskType || selectedBatches.size === 0) {
      handleApiError(
        new Error("Please select task type and at least one batch"),
      );
      return;
    }

    try {
      const taskData = {
        task_type: taskType.toLowerCase(),
        batches: Array.from(selectedBatches),
      };

      console.log("Creating task with data:", taskData);
      const result = await createTask(taskData);
      console.log(result);

      // Only show success message if task creation was successful
      if (!result.hasError) {
        handleApiSuccess("Task created successfully!");
        onTaskCreated?.();
        onClose();

        // Reset form
        setTaskType("");
        setSelectedBatches(new Set());
        setSearchBatch("");
      } else {
        handleApiError(new Error(result.message || "Failed to create task"));
      }
    } catch (error) {
      handleApiError(error, "Failed to create task");
    }
  };

  const selectedModels = Array.from(selectedBatches).reduce(
    (total, batchId) => {
      const batch = batchData.find(
        (b) => b.id === batchId || b.batch_id === batchId,
      );

      // Return different values based on task type
      if (taskType === "classification") {
        return (
          total + (batch?.Remaining_Models ?? batch?.remaining_models ?? 0)
        );
      } else if (taskType === "alignment") {
        return (
          total +
          (batch?.Remaining_Alignment ?? batch?.remaining_alignment ?? 0)
        );
      } else {
        // Default to total_models if no task type selected
        return total + (batch?.total_models || 0);
      }
    },
    0,
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay
        bg="blackAlpha.800"
        backdropFilter="blur(10px)"
        WebkitBackdropFilter="blur(10px)"
      />
      <ModalContent bg="#1C1C1C" borderColor="#3A3A3A" borderWidth="1px">
        <ModalHeader
          color="white"
          borderBottom="1px"
          borderBottomColor="#3A3A3A"
        >
          Create New Task
          <Text fontSize="sm" color="gray.400" fontWeight="normal" mt="1">
            Create a new classification or alignment task for your team
          </Text>
        </ModalHeader>
        <ModalCloseButton color="gray.400" />

        <ModalBody py="6">
          <VStack spacing="6" align="stretch">
            {/* Task Type */}
            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                Task Type
              </FormLabel>
              <Select
                placeholder="Select Type"
                value={taskType}
                onChange={(e) => setTaskType(e.target.value)}
                bg="#121212"
                borderColor="#3A3A3A"
                color="white"
                _hover={{ borderColor: "#FF9900" }}
                _focus={{
                  borderColor: "#FF9900",
                  boxShadow: "0 0 0 1px #FF9900",
                }}
              >
                <option
                  value="classification"
                  style={{ backgroundColor: "#121212" }}
                >
                  Classification
                </option>
                <option
                  value="alignment"
                  style={{ backgroundColor: "#121212" }}
                >
                  Alignment
                </option>
              </Select>
            </FormControl>

            {/* Select Batch */}
            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                Select Batches
              </FormLabel>
              <InputGroup mb="4">
                <InputLeftElement pointerEvents="none">
                  <MdSearch color="#666" />
                </InputLeftElement>
                <Input
                  placeholder="Search Batches"
                  value={searchBatch}
                  onChange={(e) => setSearchBatch(e.target.value)}
                  bg="#121212"
                  borderColor="#3A3A3A"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                  _hover={{ borderColor: "#FF9900" }}
                  _focus={{
                    borderColor: "#FF9900",
                    boxShadow: "0 0 0 1px #FF9900",
                  }}
                />
              </InputGroup>

              {/* Batch List */}
              <VStack spacing="3" align="stretch" maxH="200px" overflowY="auto">
                {batchLoading ? (
                  <Box p="4" textAlign="center">
                    <Spinner size="sm" color="orange.500" />
                    <Text color="gray.400" fontSize="sm" mt="2">
                      Loading batches...
                    </Text>
                  </Box>
                ) : filteredBatchData.length > 0 ? (
                  filteredBatchData.map((batch, index) => (
                    <HStack
                      key={
                        batch.id || batch.batch_id || batch.batch_name || index
                      }
                      p="3"
                      bg="#121212"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="#3A3A3A"
                      justify="space-between"
                    >
                      <HStack>
                        <Checkbox
                          isChecked={selectedBatches.has(
                            batch.id || batch.batch_id,
                          )}
                          onChange={() =>
                            handleBatchToggle(batch.id || batch.batch_id)
                          }
                          colorScheme="orange"
                        />
                        <Circle size="8px" bg="green.500" />
                        <VStack align="start" spacing="0">
                          <Text color="white" fontSize="sm" fontWeight="medium">
                            {batch.batch_name || "Unknown Batch"}
                          </Text>
                          <Text color="gray.400" fontSize="xs">
                            Total Models: {batch.total_models || 0} | High
                            Quality: {batch.high_quality || 0} | Not Started:{" "}
                            {batch.not_started || 0} | Verified Models:{" "}
                            {batch.Verified_Models || 0}
                          </Text>
                          <Text color="orange.300" fontSize="xs">
                            Remaining Models:{" "}
                            {batch.Remaining_Models ??
                              batch.remaining_models ??
                              0}{" "}
                            | Remaining Alignment:{" "}
                            {batch.Remaining_Alignment ??
                              batch.remaining_alignment ??
                              0}{" "}
                            | Remaining Verified Alignment:{" "}
                            {batch.Remaining_Verified_Alignment_Models ??
                              batch.Remaining_Verified_Alignment_Models ??
                              0}
                          </Text>
                        </VStack>
                      </HStack>

                      <HStack>
                        <Circle size="8px" bg="orange.500" />
                        <Text color="gray.300" fontSize="sm">
                          {batch.total_models || 0}
                        </Text>
                      </HStack>
                    </HStack>
                  ))
                ) : (
                  <Box p="4" textAlign="center" color="gray.400" fontSize="sm">
                    {searchBatch
                      ? "No batches found matching your search"
                      : "No batches available"}
                  </Box>
                )}
              </VStack>
            </FormControl>

            {/* Summary */}
            <HStack
              justify="space-between"
              pt="4"
              borderTop="1px"
              borderTopColor="#3A3A3A"
            >
              <Text color="gray.400" fontSize="sm">
                {taskType === "classification"
                  ? `Selected Models (Remaining): ${selectedModels}`
                  : taskType === "alignment"
                    ? `Selected Models (Remaining Alignment): ${selectedModels}`
                    : `Selected Models: ${selectedModels}`}
              </Text>
              <Text color="gray.400" fontSize="sm">
                Selected Batches: {selectedBatches.size}
              </Text>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px" borderTopColor="#3A3A3A">
          <Button
            variant="outline"
            mr={3}
            onClick={onClose}
            borderColor="#3A3A3A"
            color="gray.300"
            _hover={{ bg: "gray.700" }}
            isDisabled={creating}
          >
            Cancel
          </Button>
          <Button
            bg="#FF9900"
            color="black"
            _hover={{ bg: "#E68900" }}
            onClick={handleCreateTask}
            isLoading={creating}
            loadingText="Creating..."
            isDisabled={!taskType || selectedBatches.size === 0}
          >
            Create Task
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const Tasks = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [taskToDelete, setTaskToDelete] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: onDeleteConfirmOpen,
    onClose: onDeleteConfirmClose,
  } = useDisclosure();
  const navigate = useNavigate();
  const { fetchTaskData, setState } = useData();

  // Get user role for conditional rendering
  const userRole = localStorage.getItem("user_role");
  console.log("User role for testing of deployment:", userRole);
  const isAdmin = userRole === "Admin";

  // Use our custom hooks for API calls
  const { tasks, loading, error, refetch } = useTaskStats();
  const {
    deleteTask,
    unassignTask,
    loading: deletingTask,
  } = useTaskOperations();

  const handleDeleteTask = (task) => {
    setTaskToDelete(task);
    onDeleteConfirmOpen();
  };

  const handleUnassignTask = async (task) => {
    try {
      await unassignTask(task.task_id);
      handleApiSuccess(`Task "${task.task_id}" unassigned successfully!`);
      refetch(); // Refresh the task list
    } catch (error) {
      handleApiError(error, "Failed to unassign task");
    }
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    try {
      await deleteTask(taskToDelete.task_id);
      handleApiSuccess(`Task "${taskToDelete.task_id}" deleted successfully!`);
      refetch();
      onDeleteConfirmClose();
      setTaskToDelete(null);
    } catch (error) {
      handleApiError(error, "Failed to delete task");
    }
  };

  const handleTaskClick = async (task) => {
    try {
      // Set task type in context
      setState((prev) => ({
        ...prev,
        selected_task_type: task.task_type || "",
      }));
      // Only navigate, do not fetch data here
      navigate(`/tasks/${task.task_id}/models`);
    } catch (error) {
      console.error("Error loading task data:", error);
      handleApiError(error, "Failed to load task data");
    }
  };

  const handleSelectTask = (taskId) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTasks.size === tasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(tasks.map((task) => task.task_id)));
    }
  };

  const handleTaskCreated = () => {
    refetch(); // Refresh the task list after creating a new task
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch =
      task.task_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.annotated_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.reviewed_by?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType =
      !typeFilter || task.task_type === typeFilter.toLowerCase();
    return matchesSearch && matchesType;
  });

  // Show loading spinner
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <Spinner size="xl" color="orange.500" />
      </Box>
    );
  }

  // Show error message
  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>Failed to load task data: {error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb="6">
        <Flex align="center" justify="space-between">
          <Box w="full" align="center">
            <Text fontSize="3xl" fontWeight="bold" color="white" mb="2">
              Task Management
            </Text>
            <Text color="gray.400" fontSize="md">
              {isAdmin
                ? "Create and manage classification and alignment tasks"
                : "View and work on your assigned tasks"}
            </Text>
          </Box>
          {isAdmin && (
            <Button
              leftIcon={<MdAdd />}
              bg="#FF9900"
              color="black"
              _hover={{ bg: "#E68900" }}
              onClick={onOpen}
            >
              Create Task
            </Button>
          )}
        </Flex>
      </Box>

      {/* Summary Statistics */}
      {tasks.length > 0 && (
        <Box mb="6">
          <HStack spacing="6" wrap="wrap">
            <Box
              bg="#121212"
              p="4"
              borderRadius="md"
              border="1px solid #3A3A3A"
              minW="150px"
            >
              <Text color="gray.400" fontSize="sm">
                Total Tasks
              </Text>
              <Text color="white" fontSize="2xl" fontWeight="bold">
                {tasks.length}
              </Text>
            </Box>
            <Box
              bg="#121212"
              p="4"
              borderRadius="md"
              border="1px solid #3A3A3A"
              minW="150px"
            >
              <Text color="gray.400" fontSize="sm">
                Completed
              </Text>
              <Text color="green.400" fontSize="2xl" fontWeight="bold">
                {tasks.filter((task) => task.is_verified).length}
              </Text>
            </Box>
            <Box
              bg="#121212"
              p="4"
              borderRadius="md"
              border="1px solid #3A3A3A"
              minW="150px"
            >
              <Text color="gray.400" fontSize="sm">
                In Progress
              </Text>
              <Text color="orange.400" fontSize="2xl" fontWeight="bold">
                {
                  tasks.filter(
                    (task) =>
                      !task.is_verified &&
                      (task.annotated_by || task.reviewed_by),
                  ).length
                }
              </Text>
            </Box>
            <Box
              bg="#121212"
              p="4"
              borderRadius="md"
              border="1px solid #3A3A3A"
              minW="150px"
            >
              <Text color="gray.400" fontSize="sm">
                Unassigned
              </Text>
              <Text color="gray.400" fontSize="2xl" fontWeight="bold">
                {
                  tasks.filter(
                    (task) => !task.annotated_by && !task.reviewed_by,
                  ).length
                }
              </Text>
            </Box>
          </HStack>
        </Box>
      )}

      {/* Search and Filter Bar */}
      <Flex mb="6" gap="4" align="center">
        <InputGroup maxW="300px">
          <InputLeftElement pointerEvents="none">
            <MdSearch color="#666" />
          </InputLeftElement>
          <Input
            placeholder="Search Tasks"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="#121212"
            borderColor="#3A3A3A"
            color="white"
            _placeholder={{ color: "gray.500" }}
            _hover={{ borderColor: "#FF9900" }}
            _focus={{
              borderColor: "#FF9900",
              boxShadow: "0 0 0 1px #FF9900",
            }}
          />
        </InputGroup>

        <Select
          placeholder="All Types"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          maxW="200px"
          bg="#121212"
          borderColor="#3A3A3A"
          color="white"
          _hover={{ borderColor: "#FF9900" }}
          _focus={{ borderColor: "#FF9900", boxShadow: "0 0 0 1px #FF9900" }}
        >
          <option value="classification" style={{ backgroundColor: "#121212" }}>
            Classification
          </option>
          <option value="alignment" style={{ backgroundColor: "#121212" }}>
            Alignment
          </option>
        </Select>

        <Spacer />

        <Button
          leftIcon={<MdRefresh />}
          variant="outline"
          borderColor="#3A3A3A"
          color="gray.300"
          onClick={() => {
            refetch();
            handleApiSuccess("Task data refreshed");
          }}
          isLoading={loading}
          _hover={{ bg: "#FF9900", color: "black", borderColor: "#FF9900" }}
        >
          Refresh
        </Button>

        <Button
          leftIcon={<MdFilterList />}
          variant="outline"
          borderColor="#3A3A3A"
          color="gray.300"
          _hover={{ bg: "#FF9900", color: "black", borderColor: "#FF9900" }}
        >
          Filter
        </Button>
      </Flex>

      {/* Tasks Table */}
      <Box
        bg="#121212"
        borderRadius="lg"
        borderColor="#3A3A3A"
        borderWidth="1px"
        overflow="hidden"
      >
        <Table variant="simple" size="md">
          <Thead bg="#1A1A1A">
            <Tr>
              <Th borderColor="#3A3A3A" py="4">
                <Checkbox
                  isChecked={
                    selectedTasks.size === tasks.length && tasks.length > 0
                  }
                  isIndeterminate={
                    selectedTasks.size > 0 && selectedTasks.size < tasks.length
                  }
                  onChange={handleSelectAll}
                  colorScheme="orange"
                />
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
              >
                TASK NAME
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                TYPE
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                BATCH NAMES
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                TOTAL MODELS
              </Th>
              {(isAdmin || userRole === "Reviewer") && (
                <Th
                  borderColor="#3A3A3A"
                  color="gray.300"
                  fontSize="xs"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  ASSIGNED USER
                </Th>
              )}
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                PROGRESS
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                STATUS
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                ACTIONS
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredTasks.length > 0 ? (
              filteredTasks.map((task, index) => (
                <TaskRow
                  key={task.task_id || index}
                  task={task}
                  isSelected={selectedTasks.has(task.task_id)}
                  onToggle={() => handleSelectTask(task.task_id)}
                  userRole={userRole}
                  onTaskClick={handleTaskClick}
                  onDeleteTask={handleDeleteTask}
                  onUnassignTask={handleUnassignTask}
                />
              ))
            ) : (
              <Tr>
                <Td
                  colSpan={isAdmin || userRole === "Reviewer" ? "9" : "8"}
                  textAlign="center"
                  py="8"
                >
                  <Text color="gray.400" fontSize="lg">
                    {searchTerm || typeFilter
                      ? "No tasks found matching your criteria"
                      : "No tasks available"}
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Footer Info */}
      {selectedTasks.size > 0 && (
        <Box mt="4">
          <Text color="gray.400" fontSize="sm">
            {selectedTasks.size} task{selectedTasks.size !== 1 ? "s" : ""}{" "}
            selected
          </Text>
        </Box>
      )}

      {/* Create Task Modal - Only for Admins */}
      {isAdmin && (
        <CreateTaskModal
          isOpen={isOpen}
          onClose={onClose}
          onTaskCreated={handleTaskCreated}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        onClose={onDeleteConfirmClose}
        onConfirm={handleConfirmDelete}
        title="Delete Task"
        message="Are you sure you want to delete this task? This will permanently remove the task and all associated data."
        confirmText="Delete Task"
        cancelText="Cancel"
        confirmColorScheme="red"
        isLoading={deletingTask}
        loadingText="Deleting..."
        type="danger"
        itemName="Task"
        itemIdentifier={taskToDelete?.task_id}
      />
    </Box>
  );
};

export default Tasks;
